import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Circle } from '../objects/Circle';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { DrawPoint, DrawLine, DrawCircle, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

// Regular Pentagon Construction
export class PentagonConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; radius?: number } = {}): ConstructionStep[] {
    const center = { x: options.x ?? 400, y: options.y ?? 350 };
    const r = options.radius ?? 170;

    const vertices = Array.from({ length: 5 }, (_, i) => {
      const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
      return SVGRenderer.polarToCartesian(center, r, angle);
    });

    const pointO = new Point(this.renderer, center.x, center.y, 'O');
    const mainCircle = new Circle(this.renderer, center, r, false);

    const vertexPoints = vertices.map((v, idx) => new Point(this.renderer, v.x, v.y, `P${idx + 1}`));

    const edges = vertices.map((v, i) => {
      const nextV = vertices[(i + 1) % 5];
      return new Segment(this.renderer, v, nextV, false);
    });

    [pointO, mainCircle, ...vertexPoints, ...edges].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Center O and Bounding Circle',
        description: 'Place compass at O and draw circle of radius R.',
        narration: 'Step 1: Drawing bounding circle centered at O.',
        duration: 1800,
        animations: [new DrawPoint(pointO), new MoveCompass(this.compass, center, r), new DrawCircle(mainCircle, this.compass)],
      },
      {
        id: 'step2',
        title: '2. Locate 5 Pentagon Vertices P1..P5',
        description: 'Step compass along perimeter to locate 5 equal pentagon vertices.',
        narration: 'Step 2: Marking 5 equal pentagon vertices on circle perimeter.',
        duration: 2000,
        animations: vertexPoints.map((p) => new DrawPoint(p)),
      },
      {
        id: 'step3',
        title: '3. Connect Edges to Form Regular Pentagon',
        description: 'Use ruler to connect adjacent vertices.',
        narration: 'Construction complete! Regular 5-sided pentagon finished.',
        duration: 2000,
        animations: [
          new Fade(this.compass, false),
          ...edges.map((edge) => new DrawLine(edge, this.ruler)),
          ...edges.map((edge) => new Highlight(edge)),
        ],
      },
    ];
  }
}

// Regular Octagon Construction
export class OctagonConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; radius?: number } = {}): ConstructionStep[] {
    const center = { x: options.x ?? 400, y: options.y ?? 350 };
    const r = options.radius ?? 170;

    const vertices = Array.from({ length: 8 }, (_, i) => {
      const angle = (i * 2 * Math.PI) / 8;
      return SVGRenderer.polarToCartesian(center, r, angle);
    });

    const pointO = new Point(this.renderer, center.x, center.y, 'O');
    const mainCircle = new Circle(this.renderer, center, r, false);

    const vertexPoints = vertices.map((v, idx) => new Point(this.renderer, v.x, v.y, `O${idx + 1}`));

    const edges = vertices.map((v, i) => {
      const nextV = vertices[(i + 1) % 8];
      return new Segment(this.renderer, v, nextV, false);
    });

    [pointO, mainCircle, ...vertexPoints, ...edges].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Center O and Circle',
        description: 'Draw circle centered at O.',
        narration: 'Step 1: Drawing bounding circle centered at O.',
        duration: 1800,
        animations: [new DrawPoint(pointO), new MoveCompass(this.compass, center, r), new DrawCircle(mainCircle, this.compass)],
      },
      {
        id: 'step2',
        title: '2. Mark 8 Octagon Vertices',
        description: 'Mark 8 vertices on perimeter.',
        narration: 'Step 2: Marking 8 equal octagon vertices.',
        duration: 2000,
        animations: vertexPoints.map((p) => new DrawPoint(p)),
      },
      {
        id: 'step3',
        title: '3. Connect Edges to Form Regular Octagon',
        description: 'Connect adjacent vertices with ruler.',
        narration: 'Construction complete! Regular 8-sided octagon finished.',
        duration: 2200,
        animations: [
          new Fade(this.compass, false),
          ...edges.map((edge) => new DrawLine(edge, this.ruler)),
          ...edges.map((edge) => new Highlight(edge)),
        ],
      },
    ];
  }
}
